import React, { useState, useEffect, useMemo, useRef } from "react";
import "../styles/pages/AddDetails.css";
import "../styles/pages/StepColorOverrides.css";
import "../styles/pages/AddDetailsOverrides.css"; // Added for layout adjustments
import "../styles/components/FixedStepper.css"; // For fixed position stepper
import "../styles/pages/AddDetailsScrollFix.css"; // Added scroll fixes for desktop and mobile
import "../styles/pages/KeywordSuggestionsFix.css"; // Added fix for keyword suggestions display
import "../styles/pages/DirectFontOverride.css"; // Added direct override for font and spacing issues
import "../styles/pages/MobileImageCenter.css"; // Added for centering Group15183 image on mobile
import "../styles/pages/Group15183Fix.css"; // Added for Group15183 desktop positioning
import "../styles/pages/AddDetailsMobileFix.css"; // Added for form container mobile positioning
import "../styles/pages/MobileInputZoomFix.css"; // Added fix for iOS form field zooming issues
import "../styles/pages/KeywordTagFixes.css"; // Added for keyword tag styling
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { saveUserDataToGoogleSheets } from "../utils/googleSheetsService"; // Import Google Sheets service
import { generateTagline, regenerateTagline, enhanceTagline, extractKeywords } from "../services/taglineService"; // Import tagline service
import sessionTracker from "../utils/sessionTracker"; // Import session tracker
import gtmService from "../utils/gtmService"; // Import GTM service for GA4 tracking
import CacheCleanup from "../utils/cacheCleanup"; // Import cache cleanup utility
import { findCompanyByMobile } from "../services/companyDataService"; // Import company lookup function

// Add this at the top level to provide an alternative for testing
const testOpenAIAPI = async () => {
  try {
    // Create headers with more detailed configuration for project API keys
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
    };

    // If this is a project API key (starts with sk-proj-), you might need additional configuration
    if (process.env.REACT_APP_OPENAI_API_KEY.startsWith("sk-proj-")) {
      
      // Some implementations might require organization ID for project keys
      if (process.env.REACT_APP_OPENAI_ORG_ID) {
        headers["OpenAI-Organization"] = process.env.REACT_APP_OPENAI_ORG_ID;
      }
    }

    

    const response = await axios.post(
      "https://api.openai.com/v1/chat/completions",
      {
        model: "gpt-4o",
        messages: [
          {
            role: "user",
            content: "Generate a quick test response in 5 words or less.",
          },
        ],
        temperature: 0.7,
        max_tokens: 20,
      },
      { headers }
    );
    
    return "Test successful! API is working correctly.";
  } catch (error) {
    console.error("Test failed:", error);
    console.error(
      "Error details:",
      error.response ? error.response.data : "No response data"
    );

    if (error.response && error.response.status === 401) {
      return `Authentication failed: The API key appears to be invalid or expired. Status: ${error.response.status}`;
    }

    return `Test failed: ${error.message}`;
  }
};

const AddDetails = () => {
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [tagline, setTagline] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [isTaglineGenerated, setIsTaglineGenerated] = useState(false);
  const [isGeneratingTagline, setIsGeneratingTagline] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationCount, setRegenerationCount] = useState(0);
  const [industryKeywords, setIndustryKeywords] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [companies, setCompanies] = useState([]);
  const [hasCompanyData, setHasCompanyData] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companyFieldReadOnly, setCompanyFieldReadOnly] = useState(false);
  // Callback option removed as requested

  // Check if on mobile device
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  // Initialize GTM tracking for page view
  useEffect(() => {
    // Track page view in GTM for GA4
    gtmService.trackPageView('add_details', 'Add Details', window.location.pathname);
    
    // Track user journey stage
    gtmService.trackUserJourney('details_page_loaded', {
      page_type: 'form',
      step_number: 2
    });
  }, []);
  
  // Load company data from session storage if available
  useEffect(() => {
    console.log('🏢 ADDDETAILS DEBUG - Loading company data from sessionStorage');
    const companyDataString = sessionStorage.getItem('companyData');
    const currentPhoneNumber = sessionStorage.getItem('phoneNumber');
    console.log('🏢 Raw company data string:', companyDataString);
    console.log('🏢 Raw company data type:', typeof companyDataString);
    console.log('🏢 Current verified phone number:', currentPhoneNumber);
    
    // ENHANCED: Use aggressive cache cleanup utility first
    CacheCleanup.forceCleanupWithValidation(currentPhoneNumber || 'unknown', 2);
    
    // FORCE CLEAR ALL COMPANY-RELATED DATA FIRST - This ensures clean state for new customers
    setHasCompanyData(false);
    setCompanies([]);
    setCompanyFieldReadOnly(false);
    setSelectedCompany("");
    setCompanyName("");
    
    // Additional safety check: If we have company data but no verified phone number, clear it
    if (companyDataString && (!currentPhoneNumber || currentPhoneNumber === 'null')) {
      console.log('🏢 ⚠️ Company data exists but no verified phone number - using aggressive cleanup');
      CacheCleanup.forceCleanupWithValidation('no-phone-number', 3);
      setHasCompanyData(false);
      return;
    }
    
    // 🔧 FIXED LOGIC: Check sessionStorage FIRST, then hardcoded list
    // Check if there's valid company data in sessionStorage (from API/CSV)
    if (companyDataString && companyDataString !== 'null' && companyDataString !== 'undefined' && companyDataString !== '[]') {
      try {
        const companyData = JSON.parse(companyDataString);
        console.log('🏢 Found company data in sessionStorage:', companyData);
        
        // Validate that we have actual company data with proper structure
        if (companyData && Array.isArray(companyData) && companyData.length > 0) {
          const firstCompany = companyData[0];
          if (firstCompany && (firstCompany.companyName || firstCompany.company_name)) {
            console.log('🏢 ✅ Valid company data from API/CSV found - Using it');
            setCompanies(companyData);
            setHasCompanyData(true);
            
            // If there's only one company, set it as selected and make field read-only
            if (companyData.length === 1) {
              const companyName = firstCompany.companyName || firstCompany.company_name;
              console.log('🏢 Single company found, setting as read-only:', companyName);
              setSelectedCompany(companyName);
              setCompanyName(companyName);
              setCompanyFieldReadOnly(true);
              
              // 🔧 FORCE RE-RENDER: Use setTimeout to ensure state update is processed
              setTimeout(() => {
                setCompanyName(companyName);
                console.log('🔧 FORCED companyName state update:', companyName);
              }, 50);
            } else {
              console.log('🏢 Multiple companies found, showing dropdown');
            }
            
            // Exit early - we found valid data
            console.log('🏢 ✅ Using API/CSV company data successfully');
            return;
          }
        }
      } catch (error) {
        console.error("🏢 ❌ Error parsing company data from sessionStorage:", error);
      }
    }
    
    // If no valid sessionStorage data, check if phone is in hardcoded business list
    console.log('🏢 No valid sessionStorage data, checking hardcoded business list...');
    const shouldHaveCompany = CacheCleanup.shouldHaveCompanyData(currentPhoneNumber);
    if (!shouldHaveCompany) {
      console.log('🏢 📱 Phone number not in business list - ensuring clean state');
      CacheCleanup.forceCleanupWithValidation(currentPhoneNumber, 3);
      setHasCompanyData(false);
      return;
    }
    
    // ENHANCED: If phone is in business list but no sessionStorage data, check CSV
    console.log('🏢 📋 Phone number is in business list, checking CSV for company data...');
    const csvCompanies = findCompanyByMobile(currentPhoneNumber);
    console.log('🏢 📋 CSV lookup result:', csvCompanies);
    
    if (csvCompanies && csvCompanies.length > 0) {
      console.log('🏢 ✅ Found company data in CSV for business number:', currentPhoneNumber);
      setCompanies(csvCompanies);
      setHasCompanyData(true);
      
      // If there's only one company, set it as selected and make field read-only
      if (csvCompanies.length === 1) {
        const companyName = csvCompanies[0].companyName;
        console.log('🏢 Single company found in CSV, setting as read-only:', companyName);
        setSelectedCompany(companyName);
        setCompanyName(companyName);
        setCompanyFieldReadOnly(true);
        
        // 🔧 FORCE RE-RENDER: Use setTimeout to ensure state update is processed (CSV section)
        setTimeout(() => {
          setCompanyName(companyName);
          console.log('🔧 FORCED CSV companyName state update:', companyName);
        }, 10);
        
        // CRITICAL FIX: Save company name to sessionStorage immediately for SharePoster
        sessionStorage.setItem("companyName", companyName);
        console.log('🏢 💾 SAVED company name to sessionStorage for SharePoster:', companyName);
      } else {
        console.log('🏢 Multiple companies found in CSV, showing dropdown');
      }
    } else {
      console.log('🏢 ❌ No company data found in CSV for business number:', currentPhoneNumber);
      setHasCompanyData(false);
    }
    
    // Log final state after a short delay to see actual state
    setTimeout(() => {
      console.log('🏢 📊 FINAL STATE SUMMARY:');
      console.log('🏢 hasCompanyData:', hasCompanyData);
      console.log('🏢 companies:', companies);
      console.log('🏢 selectedCompany:', selectedCompany);
      console.log('🏢 companyFieldReadOnly:', companyFieldReadOnly);
      console.log('🏢 sessionStorage companyData:', sessionStorage.getItem('companyData'));
    }, 100);
  }, []);
  
  // Apply mobile form positioning override
  useEffect(() => {
    if (isMobile) {
      // Force proper form container margin on mobile
      setTimeout(() => {
        try {
          const formContainer = document.querySelector('.form-container');
          if (formContainer) {
            formContainer.style.setProperty('margin-top', '-65px', 'important');
            formContainer.style.setProperty('z-index', '100', 'important');
            formContainer.style.setProperty('width', 'calc(100%)', 'important');
            formContainer.style.setProperty('margin-left', '5px', 'important');
            formContainer.style.setProperty('margin-right', '5px', 'important');
            
          }
        } catch (err) {
          console.error('Error applying form container position:', err);
        }
      }, 100); // Short delay to ensure DOM is ready
    }
  }, [isMobile]);

  const navigate = useNavigate();

  // Industry keywords mapping
  const industryKeywordsMap = useMemo(
    () => ({
      manufacturing: [
        "Precision",
        "Innovation",
        "Quality",
        "Efficiency",
        "Reliability",
        "Engineering",
        "Scalability",
        "Sustainability",
        "Technology",
        "Durability",
        "Craftsmanship",
        "Process",
        "Customization",
        "Performance",
        "Industrial",
      ],
      retail: [
        "Value",
        "Selection",
        "Savings",
        "Trusted",
        "Quick",
        "Supply",
        "Accessible",
        "Everyday",
        "Inventory",
        "Price",
        "Wholesale",
        "Choice",
        "Convenience",
        "Reliable",
        "Deals",
      ],
      services: [
        "Professional",
        "Expertise",
        "Support",
        "Trusted",
        "Efficient",
        "Personalized",
        "On-demand",
        "Client-first",
        "Solutions",
        "Seamless",
        "Affordable",
        "Timely",
        "Hassle-free",
        "Quality",
        "Results",
      ],
      food: [
        "Fresh",
        "Flavor",
        "Authentic",
        "Taste",
        "Nourish",
        "Handcrafted",
        "Sourced",
        "Delight",
        "Organic",
        "Savory",
        "Experience",
        "Indulgent",
        "Pure",
        "Healthy",
        "Joy",
      ],
      construction: [
        "Solid",
        "Vision",
        "Build",
        "Space",
        "Modern",
        "Reliable",
        "Landmark",
        "Property",
        "Secure",
        "Smart",
        "Design",
        "Investment",
        "Durable",
        "Elegant",
        "Foundation",
      ],
      healthcare: [
        "Care",
        "Healing",
        "Wellness",
        "Trust",
        "Relief",
        "Safe",
        "Expert",
        "Recovery",
        "Precision",
        "Compassion",
        "Advanced",
        "Support",
        "Health",
        "Modern",
        "Secure",
      ],
      agriculture: [
        "Organic",
        "Growth",
        "Sustainable",
        "Farm-fresh",
        "Pure",
        "Harvest",
        "Natural",
        "Soil",
        "Season",
        "Cultivate",
        "Roots",
        "Crop",
        "Rural",
        "Nourish",
        "Yield",
      ],
      education: [
        "Learn",
        "Grow",
        "Future",
        "Empower",
        "Skill",
        "Inspire",
        "Knowledge",
        "Discover",
        "Teach",
        "Guide",
        "Success",
        "Bright",
        "Innovate",
        "Mentor",
        "Achieve",
      ],
      transport: [
        "Fast",
        "Route",
        "Safe",
        "On-time",
        "Network",
        "Global",
        "Ship",
        "Track",
        "Fleet",
        "Delivery",
        "Cargo",
        "Efficient",
        "Move",
        "Road",
        "Connect",
      ],
      technology: [
        "Code",
        "Digital",
        "Secure",
        "Scalable",
        "Smart",
        "Agile",
        "AI",
        "Data",
        "Cloud",
        "System",
        "Future",
        "Transform",
        "Fast",
        "Innovation",
        "Integrate",
      ],
      tourism: [
        "Escape",
        "Comfort",
        "Explore",
        "Culture",
        "Luxury",
        "Stay",
        "Experience",
        "Unwind",
        "Journey",
        "Local",
        "Discover",
        "Memorable",
        "Adventure",
        "Delight",
        "Warmth",
      ],
      fashion: [
        "Style",
        "Trend",
        "Bold",
        "Fit",
        "Fabric",
        "Modern",
        "Timeless",
        "Iconic",
        "Look",
        "Wear",
        "Couture",
        "Design",
        "Wardrobe",
        "Identity",
        "Chic",
      ],
      events: [
        "Celebrate",
        "Plan",
        "Seamless",
        "Wow",
        "Custom",
        "Create",
        "Detail",
        "Stage",
        "Experience",
        "Flow",
        "Decor",
        "Moment",
        "Memory",
        "Design",
        "Magic",
      ],
      ecommerce: [
        "Online",
        "Browse",
        "Click",
        "Fast",
        "Value",
        "Trusted",
        "Discover",
        "Store",
        "Digital",
        "Product",
        "Global",
        "Quick",
        "Smart",
        "Trendy",
        "Delivered",
      ],
      printing: [
        "Print",
        "Package",
        "Custom",
        "Design",
        "Bold",
        "Finish",
        "Durable",
        "Detail",
        "Color",
        "Material",
        "Shape",
        "Label",
        "Eco",
        "Protect",
        "Visual",
      ],
      beauty: [
        "Glow",
        "Natural",
        "Radiant",
        "Balance",
        "Pure",
        "Skin",
        "Bliss",
        "Revive",
        "Youth",
        "Ritual",
        "Beauty",
        "Calm",
        "Feel",
        "Care",
        "Inside-out",
      ],
      automotive: [
        "Drive",
        "Smooth",
        "Power",
        "Safe",
        "Tune",
        "Reliable",
        "Garage",
        "Speed",
        "Engine",
        "Ride",
        "Mileage",
        "Precision",
        "Service",
        "Expert",
        "On-road",
      ],
      media: [
        "Buzz",
        "Impact",
        "Creative",
        "Story",
        "Bold",
        "Brand",
        "Viral",
        "Message",
        "Insight",
        "Reach",
        "Idea",
        "Visual",
        "Engage",
        "Campaign",
        "Voice",
      ],
      cleaning: [
        "Sparkle",
        "Fresh",
        "Hygienic",
        "Deep-clean",
        "Safe",
        "Tidy",
        "Reliable",
        "Sanitize",
        "Shine",
        "Spotless",
        "Green",
        "Purify",
        "Swift",
        "Trust",
        "Clean",
      ],
      handicrafts: [
        "Handmade",
        "Craft",
        "Authentic",
        "Local",
        "Unique",
        "Art",
        "Heritage",
        "Tradition",
        "Detail",
        "Natural",
        "Skilled",
        "Touch",
        "Inspired",
        "Culture",
        "Crafted",
      ],
      other: [
        "Trusted",
        "Passion",
        "Smart",
        "Driven",
        "Unique",
        "Growth",
        "Bold",
        "Modern",
        "Vision",
        "Local",
        "Global",
        "Agile",
        "Creative",
        "Experience",
        "Legacy",
      ],
    }),
    []
  );

  // Update industry keywords when industry changes AND reset selected keywords
  useEffect(() => {
    if (industry) {
      setIndustryKeywords(industryKeywordsMap[industry] || []);
      // Reset keywords when industry changes to fix the issue with keywords not being reset
      setKeywords([]);
      // Reset tagline when industry changes
      setTagline("");
      setIsTaglineGenerated(false);
    } else {
      setIndustryKeywords([]);
    }
  }, [industry, industryKeywordsMap]);

  // List of negative keywords to filter out from name field
  const negativeKeywords = [
    // Original keywords
    "private",
    "limited",
    "ltd",
    "clinic",
    "corporate",
    "company",
    // Added new keywords
    "associates",
    "fin",
    "corp",
    "solutions",
    "services",
    "pvt",
    "finserv",
    "finance",
    "loans",
    "fincorp",
    "enterprise",
    "credit",
    "llp",
    "consulting",
    "partner",
    "partnership",
    "credits",
    "public",
    "technology",
    "tech",
    "fincon",
    "care",
    "marketing",
    "sufin",
    "l&t finance",
    "ltf",
    "lt finance",
    "L&T",
    // Add more abusive words or unwanted terms here
  ];

  // Function to check and filter name input
  const filterNameInput = (input) => {
    // Convert to lowercase for case-insensitive comparison
    const lowerInput = input.toLowerCase();

    // Check if input contains any negative keywords
    const containsNegativeKeyword = negativeKeywords.some((keyword) =>
      lowerInput.includes(keyword.toLowerCase())
    );

    if (containsNegativeKeyword) {
      // Alert user about invalid input
      alert(
        'Please avoid using business terms like "private", "limited", "ltd", "corp", "finance", "services", etc. in the name field. Enter your personal name only.'
      );
      return false;
    }

    return true;
  };

  // Handle adding a keyword - limited to max 5 keywords
  const handleAddKeyword = (keyword) => {
    if (keyword && !keywords.includes(keyword)) {
      if (keywords.length < 5) {
        setKeywords([...keywords, keyword]);
        setKeywordInput("");
      } else {
        alert("You can select a maximum of 5 keywords");
      }
    }
  };

  // Handle removing a keyword
  const handleRemoveKeyword = (keywordToRemove) => {
    setKeywords(keywords.filter((keyword) => keyword !== keywordToRemove));
  };

  // Handle key press in keyword input
  const handleKeywordKeyPress = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddKeyword(keywordInput.trim());
    }
  };

  // Add a button to test the API
  const handleTestAPI = async () => {
    try {
      
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      };

      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-4o",
          messages: [
            {
              role: "user",
              content: "Generate a quick test response in 5 words or less.",
            },
          ],
          temperature: 0.7,
          max_tokens: 20,
        },
        { headers }
      );

      const result =
        "Test successful! API response: " +
        response.data.choices[0].message.content;
      alert(result);
      return result;
    } catch (error) {
      console.error("API test failed:", error);
      alert(`Test failed: ${error.message}`);
      return `Test failed: ${error.message}`;
    }
  };

  // Generate tagline using taglineService
  const handleGenerateTagline = async () => {
    if (keywords.length === 0) {
      alert("Please add at least one keyword to generate a tagline");
      return;
    }

    // Limit to max 5 keywords
    if (keywords.length > 5) {
      alert("Please select a maximum of 5 keywords");
      return;
    }
    
    // Store the selected keywords in session storage for use in the final poster
    sessionStorage.setItem('selectedKeywords', JSON.stringify(keywords));

    // Check if we're regenerating
    const isRegeneration = isTaglineGenerated;
    if (isRegeneration) {
      setIsRegenerating(true);
      setRegenerationCount((prevCount) => prevCount + 1);
    }

    setIsGeneratingTagline(true);

    try {
      
      
      // Create user info object for tagline generation
      const userInfo = {
        industry: industry || "business",
        keywords: keywords
      };
      
      // Call the appropriate tagline service function based on whether we're regenerating
      let result;
      if (isRegeneration) {
        result = await regenerateTagline(userInfo, regenerationCount);
      } else {
        result = await generateTagline(userInfo);
      }
      
      if (result.success && result.tagline) {
        // Enhance tagline by highlighting keywords if not already highlighted
        let finalTagline = result.tagline;
        
        // Check if tagline already has highlights (asterisks)
        if (!finalTagline.includes('*')) {
          // If not, enhance it with our keyword highlighting
          finalTagline = enhanceTagline(finalTagline, keywords);
        }
        
        
        
        // Store highlighted keywords in session storage for the poster
        const highlightRegex = /\*(.*?)\*/g;
        const highlightedKeywords = [];
        let match;
        
        while ((match = highlightRegex.exec(finalTagline)) !== null) {
          highlightedKeywords.push(match[1]);
        }
        
        if (highlightedKeywords.length > 0) {
          
          sessionStorage.setItem('highlightedKeywords', JSON.stringify(highlightedKeywords));
        }
        
        setTagline(finalTagline);
        setIsTaglineGenerated(true);
        
        // Track successful tagline generation in GTM for GA4
        gtmService.trackFormSubmission('tagline_generation', {
          industry: industry,
          keywords_count: keywords.length,
          generation_type: isRegeneration ? 'regeneration' : 'initial',
          regeneration_count: isRegeneration ? regenerationCount : 0,
          highlighted_keywords_count: highlightedKeywords.length
        });
      } else {
        console.error("Tagline generation failed:", result.error);
        
        // Track failed tagline generation in GTM for GA4
        gtmService.trackError('tagline_generation_failed', result.error || 'Unknown API error', 'handleGenerateTagline');
        
        // Fall back to the local generation if API call fails
        generateFallbackTagline();
      }
    } catch (error) {
      console.error("Error generating tagline:", error);
      
      // Track tagline generation error in GTM for GA4
      gtmService.trackError('tagline_generation_exception', error.message, 'handleGenerateTagline');
      
      // Fall back to the local generation if API call fails
      generateFallbackTagline();
    } finally {
      setIsGeneratingTagline(false);
      setIsRegenerating(false);
    }
  };

  // Fallback tagline generation in case API doesn't work
  const generateFallbackTagline = () => {
    // Enhanced industry-specific tagline templates with placeholders for up to 5 keywords
    const industryTaglines = {
      manufacturing: {
        templates: [
          "I craft *{keyword1}* products with *{keyword2}* *{keyword3}* and *{keyword4}* *{keyword5}* expertise",
          "I deliver *{keyword1}* *{keyword2}* manufacturing with *{keyword3}* *{keyword4}* and *{keyword5}* quality",
          "I provide *{keyword1}* *{keyword2}* *{keyword3}* solutions with *{keyword4}* and *{keyword5}* precision",
        ],
      },
      retail: {
        templates: [
          "I create *{keyword1}* *{keyword2}* shopping with *{keyword3}* *{keyword4}* and *{keyword5}* service",
          "I offer *{keyword1}* *{keyword2}* products with *{keyword3}* *{keyword4}* and *{keyword5}* care",
          "I deliver *{keyword1}* *{keyword2}* *{keyword3}* retail with *{keyword4}* and *{keyword5}* satisfaction",
        ],
      },
      services: {
        templates: [
          "I provide *{keyword1}* *{keyword2}* services with *{keyword3}* *{keyword4}* and *{keyword5}* expertise",
          "I deliver *{keyword1}* *{keyword2}* *{keyword3}* solutions with *{keyword4}* and *{keyword5}* dedication",
          "I offer *{keyword1}* *{keyword2}* support with *{keyword3}* *{keyword4}* and *{keyword5}* approaches",
        ],
      },
      finance: {
        templates: [
          "I build *{keyword1}* *{keyword2}* futures with *{keyword3}* *{keyword4}* and *{keyword5}* strategies",
          "I create *{keyword1}* *{keyword2}* *{keyword3}* portfolios with *{keyword4}* and *{keyword5}* guidance",
          "I provide *{keyword1}* *{keyword2}* financial plans with *{keyword3}* *{keyword4}* and *{keyword5}* solutions",
        ],
      },
      technology: {
        templates: [
          "I transform *{keyword1}* businesses with *{keyword2}* *{keyword3}* *{keyword4}* and *{keyword5}* solutions",
          "I develop *{keyword1}* *{keyword2}* systems with *{keyword3}* *{keyword4}* and *{keyword5}* innovation",
          "I deliver *{keyword1}* *{keyword2}* *{keyword3}* experiences with *{keyword4}* and *{keyword5}* expertise",
        ],
      },
      // Default templates for other industries
      default: {
        templates: [
          "I deliver *{keyword1}* *{keyword2}* results with *{keyword3}* *{keyword4}* and *{keyword5}* expertise",
          "I provide *{keyword1}* *{keyword2}* *{keyword3}* solutions with *{keyword4}* and *{keyword5}* quality",
          "I create *{keyword1}* *{keyword2}* experiences with *{keyword3}* *{keyword4}* and *{keyword5}* service",
        ],
      },
    };

    // Function to fill template with actual keywords
    const fillTemplate = (template, keywordsToUse) => {
      let result = template;
      keywordsToUse.forEach((keyword, index) => {
        const placeholder = `*{keyword${index + 1}}*`;
        result = result.replace(placeholder, `*${keyword}*`);
      });
      // Remove any remaining placeholders
      result = result.replace(/\*\{keyword\d+\}\*/g, "");
      // Clean up multiple spaces
      result = result.replace(/\s+/g, " ").trim();
      return result;
    };

    // If no keywords are available, use these generic ones
    const genericKeywords = [
      "innovative",
      "reliable",
      "professional",
      "quality",
      "trusted",
      "expert",
      "dedicated",
      "exceptional",
      "customized",
      "comprehensive",
    ];

    // Get appropriate templates for the industry or use default
    const templateSet = industryTaglines[industry] || industryTaglines.default;
    const templates = templateSet.templates;

    // Choose a random template
    const selectedTemplate =
      templates[Math.floor(Math.random() * templates.length)];

    // Prepare keywords to use
    let keywordsToUse = [];

    if (keywords.length > 0) {
      // Use all user keywords
      keywordsToUse = [...keywords];
      
      // If we have less than 5 keywords, we'll fill the rest with generics
      if (keywordsToUse.length < 5) {
        // Get generic keywords that are not already in user keywords
        const availableGenericKeywords = genericKeywords.filter(
          generic => !keywords.includes(generic)
        );
        // Shuffle them
        const shuffledGeneric = [...availableGenericKeywords].sort(() => 0.5 - Math.random());
        // Take what we need to fill up to 5
        const additionalKeywords = shuffledGeneric.slice(0, 5 - keywordsToUse.length);
        // Add to our keywords
        keywordsToUse.push(...additionalKeywords);
      }
    } else {
      // No user keywords, use 5 generic ones
      const shuffled = [...genericKeywords].sort(() => 0.5 - Math.random());
      keywordsToUse = shuffled.slice(0, 5);
    }

    // Generate the final tagline
    const generatedTagline = fillTemplate(selectedTemplate, keywordsToUse);

    // Save the tagline to state
    setTagline(generatedTagline);
    setIsTaglineGenerated(true);
    
    // Save to session storage
    sessionStorage.setItem('tagline', generatedTagline);
    
    // Extract and save highlighted keywords
    try {
      const highlightedWords = [];
      const regex = /\*(.*?)\*/g;
      let match;
      while ((match = regex.exec(generatedTagline)) !== null) {
        if (match[1] && match[1].trim().length > 0) {
          highlightedWords.push(match[1].trim());
        }
      }
      
      // If we found highlighted words in the tagline, save them
      if (highlightedWords.length > 0) {
        sessionStorage.setItem('highlightedKeywords', JSON.stringify(highlightedWords));
      }
    } catch (e) {
      console.error('Error extracting highlighted keywords:', e);
    }
  };

  // Reset keywords when industry changes
  useEffect(() => {
    // Clear selected keywords when industry changes
    setKeywords([]);
    setTagline("");
    setIsTaglineGenerated(false);
  }, [industry]);

  // State for validation errors
  const [errors, setErrors] = useState({});

  // Handle name input with filtering
  const handleNameChange = (e) => {
    const value = e.target.value;
    if (filterNameInput(value)) {
      setName(value);
    }
  };

  // Handle next button click
  const handleNext = async () => {
    // Validate form fields
    const validationErrors = {};
    
    if (!name.trim()) validationErrors.name = "Name is required";
    // Validate company selection if company data exists and multiple companies
    if (hasCompanyData && companies.length > 1 && !selectedCompany) {
      validationErrors.company = "Please select a company";
    }
    if (!industry) validationErrors.industry = "Please select an industry";
    if (keywords.length === 0) validationErrors.keywords = "Please select at least one keyword";
    if (!tagline) validationErrors.tagline = "Please generate a tagline";
    
    // If there are validation errors, display them and stop form submission
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      alert("Please fill in all required fields");
      return;
    }
    
    // Clear any previous errors
    setErrors({});

    try {
      // Track form submission in GTM for GA4
      gtmService.trackFormSubmission('user_details', {
        industry: industry,
        keywords_count: keywords.length,
        has_company_data: hasCompanyData,
        company_readonly: companyFieldReadOnly,
        tagline_generated: isTaglineGenerated
      });
      
      // Track form completion with session tracker
      await sessionTracker.trackUserDetails({
        name: name,
        companyName: hasCompanyData ? 
          (companyFieldReadOnly ? (selectedCompany || companyName) : selectedCompany) : 
          '', // Empty string for new customers with no company data
        industry: industry,
        keywords: keywords,
        tagline: tagline,
        keywordCount: keywords.length
      });

      // Update session stage to indicate form completion
      await sessionTracker.updateSessionStage('form_completed');
    } catch (trackingError) {
      console.error("Session tracking error:", trackingError);
      // Continue with form submission even if tracking fails
    }

    // Store form data in sessionStorage instead of localStorage
    sessionStorage.setItem("userName", name);
    
    // CRITICAL FIX: Enhanced company name handling for new vs existing customers
    if (hasCompanyData && companyFieldReadOnly) {
      // Existing customer with single company - use that company name
      const companyToStore = selectedCompany || companyName;
      sessionStorage.setItem("companyName", companyToStore);
      console.log('🏢 Storing company name for existing customer (single company):', companyToStore);
    } else if (hasCompanyData && selectedCompany) {
      // Existing customer with multiple companies - use selected company
      sessionStorage.setItem("companyName", selectedCompany);
      console.log('🏢 Storing company name for existing customer (selected from dropdown):', selectedCompany);
    } else {
      // Check if this is a business number that should have company data
      const currentPhoneNumber = sessionStorage.getItem('phoneNumber');
      const shouldHaveCompany = CacheCleanup.shouldHaveCompanyData(currentPhoneNumber);
      
      if (shouldHaveCompany) {
        // BUSINESS CUSTOMER but no company data selected - try to get from CSV
        const csvCompanies = findCompanyByMobile(currentPhoneNumber);
        if (csvCompanies && csvCompanies.length > 0) {
          const companyToStore = csvCompanies[0].companyName;
          sessionStorage.setItem("companyName", companyToStore);
          console.log('🏢 BUSINESS CUSTOMER: Found and storing company name from CSV:', companyToStore);
        } else {
          // Business number but no company found - remove any stale data
          sessionStorage.removeItem("companyName");
          console.log('🏢 BUSINESS CUSTOMER: No company data found, removed company name from sessionStorage');
        }
      } else {
        // NEW CUSTOMER - Absolutely ensure NO company name is stored
        sessionStorage.removeItem("companyName");
        console.log('🏢 NEW CUSTOMER: Removed company name from sessionStorage for non-business number');
        
        // Double-check and force clear any potential company name value
        setTimeout(() => {
          sessionStorage.removeItem("companyName");
        }, 100);
      }
    }
    sessionStorage.setItem("industry", industry);
    
    // Store the original tagline with asterisks for backend processing
    sessionStorage.setItem("tagline", tagline);

    // Also store a clean version of the tagline without asterisks
    const cleanTagline = tagline.replace(/\*(.*?)\*/g, "$1");
    sessionStorage.setItem("cleanTagline", cleanTagline);

    // Store keywords as JSON string - using selectedKeywords key for consistency with SharePoster.js
    sessionStorage.setItem("selectedKeywords", JSON.stringify(keywords));

    // Identify any highlighted keywords based on tagline content
    const highlightedWords = keywords.filter((keyword) => {
      // Check if this keyword appears in the tagline (case insensitive)
      const keywordRegex = new RegExp(`\\*?${keyword}\\*?`, "i");
      return keywordRegex.test(tagline);
    });
    
    // Store the highlighted keywords separately for the poster generation
    sessionStorage.setItem(
      "highlightedKeywords",
      JSON.stringify(highlightedWords)
    );
    
    // Save the form data to Google Sheets
    try {
      // Prepare the user data object
      const userData = {
        name: name,
        companyName: hasCompanyData ? (companyFieldReadOnly ? (selectedCompany || companyName) : selectedCompany) : '', // Only set company name if company data exists
        industry: industry,
        keywords: keywords,
        tagline: cleanTagline, // Use the clean version without asterisks
        // Email and phone could be added here if they were collected in this form
      };
      
      
      
      // Save to Google Sheets using the service
      // We'll do this asynchronously but not wait for completion to avoid delaying navigation
      saveUserDataToGoogleSheets(userData)
        .then(result => {
          
        })
        .catch(error => {
          console.error("Error saving data to Google Sheets:", error);
          // Continue with navigation even if Google Sheets save fails
        });
    } catch (error) {
      console.error("Error preparing data for Google Sheets:", error);
      // Don't block navigation if there's an error with Google Sheets
    }

    // Move to the upload photo page
    navigate("/upload-photo");
  };

  // Common input style to ensure Poppins font
  const inputStyle = {
    fontFamily: "Poppins, sans-serif",
  };

  // Style for yellow button
  const yellowButtonStyle = {
    backgroundColor: "#FFC107",
    color: "#000000",
  };

  // Handle tagline selection
  const taglineRef = useRef(null);

  // Log when regenerate button is clicked
  useEffect(() => {
    if (isRegenerating) {
      
    }
  }, [isRegenerating, regenerationCount]);

  // Add scroll prevention for dropdowns
  useEffect(() => {
    // Prevent scrolling when dropdown is opened
    const handleSelectFocus = (e) => {
      // Save current scroll position
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // Add class to body to indicate a select is open
      document.body.classList.add('select-open');
      
      // Store the scroll position as a data attribute
      document.body.setAttribute('data-scroll-position', scrollPosition);
    };
    
    const handleSelectBlur = () => {
      // Remove class from body
      document.body.classList.remove('select-open');
      
      // Restore scroll position
      const scrollPosition = parseInt(document.body.getAttribute('data-scroll-position') || '0', 10);
      window.scrollTo(0, scrollPosition);
    };
    
    // Attach event listeners to all select elements
    const selectElements = document.querySelectorAll('select');
    selectElements.forEach(select => {
      select.addEventListener('mousedown', handleSelectFocus);
      select.addEventListener('touchstart', handleSelectFocus);
      select.addEventListener('focus', handleSelectFocus);
      select.addEventListener('change', handleSelectBlur);
      select.addEventListener('blur', handleSelectBlur);
    });
    
    // Clean up event listeners on component unmount
    return () => {
      selectElements.forEach(select => {
        select.removeEventListener('mousedown', handleSelectFocus);
        select.removeEventListener('touchstart', handleSelectFocus);
        select.removeEventListener('focus', handleSelectFocus);
        select.removeEventListener('change', handleSelectBlur);
        select.removeEventListener('blur', handleSelectBlur);
      });
      document.body.classList.remove('select-open');
    };
  }, []);

  return (
    <div
      className="details-page"
      style={
        isMobile
          ? {
              backgroundImage: `url('/images/adddetails/UploadPhoto+AddDetails.png')`,
            }
          : {}
      }
    >
      {" "}
      {!isMobile && (
        <div className="left-section">
          <img
            src="/images/adddetails/UploadPhoto+AddDetails.png"
            alt="Background"
            className="left-section-background"
          />
          <div className="left-logo-container">
            <Link to="/">
              <img
                src="/images/adddetails/LOGO.png?v=20241118"
                alt="L&T Finance Logo"
                className="left-logo-image"
                style={{ 
                  paddingTop: "20px",
                  margin: "0 auto",
                  display: "block"
                }}
              />{" "}
            </Link>{" "}
          </div>{" "}
          <div className="left-group-container">
            <img
              src="/images/adddetails/Group15183.png"
              alt="Group"
              className="left-group-image"
              style={{ marginTop: "-30px" }}
            />{" "}
          </div>{" "}
          <div className="left-people-container">
            <img
              src="/images/adddetails/Layer1.png"
              alt="Layer"
              className="left-people-image"
              style={{ marginTop: "-25px" }}
            />{" "}
          </div>{" "}
        </div>
      )}
      <div className="right-section">
        {" "}
        {/* Fixed position stepper */}{" "}
        <div className="fixed-stepper-container">
          <div className="fixed-stepper">
            <div className="progress-step completed">
              <div className="step-circle"> 1 </div>{" "}
              <div className="step-label"> OTP </div>{" "}
            </div>{" "}
            <div className="progress-line active"> </div>{" "}
            <div className="progress-step active">
              <div className="step-circle"> 2 </div>{" "}
              <div className="step-label"> Add Details </div>{" "}
            </div>{" "}
            <div className="progress-line active"> </div>{" "}
            <div className="progress-step">
              <div className="step-circle"> 3 </div>{" "}
              <div className="step-label"> Upload </div>{" "}
            </div>{" "}
          </div>{" "}
        </div>
        <div className="right-content">
          {" "}
          {/* Mobile elements container */}{" "}
          {isMobile && (
            <div
              className="mobile-elements-container"
              style={{ marginBottom: "0px" }}
            >
              <div style={{
                width: "100%", 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center",
                margin: "0 auto",
                paddingBottom: "25px"
              }}>
                <Link to="/" style={{ 
                  display: "inline-block", 
                  lineHeight: 0
                }}>
                  <img
                    src="/images/adddetails/LOGO.png?v=20241118"
                    alt="L&T Finance Logo"
                    className="mobile-logo"
                    style={{ 
                      width: "200px", 
                      height: "auto",
                      display: "block",
                      margin: "0 auto",
                      padding: "0",
                      maxWidth: "200px"
                    }}
                  />
                </Link>
              </div>
              <div style={{
                width: "100%",
                textAlign: "center",
                marginTop: "0px"
              }}>
                <img
                  src="/images/adddetails/Group15183.png"
                  alt="Group"
                  className="mobile-group"
                  style={{ 
                    display: "inline-block",
                    width: "80%",
                    maxWidth: "300px"
                  }}
                />
              </div>
              <div style={{
                width: "100%",
                textAlign: "center",
                marginTop: "-20px",
                marginBottom: "0px"
              }}>
                <img
                  src="/images/adddetails/Layer1.png"
                  alt="Layer"
                  className="mobile-layer"
                  style={{
                    display: "inline-block",
                    width: "90%",
                    maxWidth: "390px",
                    zIndex: 1
                  }}
                />
              </div>
            </div>
          )}
          <div
            className="form-container"
            style={{
              marginBottom: "-20px",
              marginTop: isMobile ? "-65px" : "0px",
              position: "relative",
              zIndex: 100,
              minHeight: isMobile ? "auto" : "700px", /* Reduced from 800px to 700px */
              height: isMobile ? "auto" : "auto", /* Allow expansion beyond minimum on desktop if needed */
              overflow: "visible",
              overflowY: "visible",
              width: isMobile ? "calc(100% - 10px)" : "100%",
              marginLeft: isMobile ? "5px" : "0",
              marginRight: isMobile ? "5px" : "0"
            }}
          >
            {" "}
            {/* Mobile stepper inside form */}{" "}
            {isMobile && (
              <div className="mobile-stepper-container form-stepper">
                <div className="mobile-stepper">
                  <div className="progress-step completed">
                    <div className="step-circle"> 1 </div>{" "}
                    <div className="step-label"> OTP </div>{" "}
                  </div>{" "}
                  <div className="progress-line active"> </div>{" "}
                  <div className="progress-step active">
                    <div className="step-circle"> 2 </div>{" "}
                    <div className="step-label"> Add Details </div>{" "}
                  </div>{" "}
                  <div className="progress-line"> </div>{" "}
                  <div className="progress-step">
                    <div className="step-circle"> 3 </div>{" "}
                    <div className="step-label"> Upload </div>{" "}
                  </div>{" "}
                </div>{" "}
              </div>
            )}
            <h1 className="form-title"> Add your Details </h1>
            {/* Name and Industry in one row */}{" "}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name"> Name <span style={{ color: "red" }}>*</span> </label>{" "}
                <input
                  type="text"
                  id="name"
                  placeholder="Your Name"
                  value={name}
                  onChange={handleNameChange}
                  className="form-input border-blue"
                  style={inputStyle}
                />{" "}
              </div>
              
              {/* Company field - conditionally shown only if company data exists */}
              {hasCompanyData && (
                <div className="form-group">
                  <label htmlFor="company"> Company <span style={{ color: "red" }}>*</span> </label>{" "}
                  {companies.length === 1 ? (
                    // Single company - show as read-only field
                    <input
                      type="text"
                      id="company"
                      value={companyName}
                      readOnly
                      className="form-input border-blue"
                      style={{...inputStyle, backgroundColor: "#f5f5f5"}}
                    />
                  ) : (
                    // Multiple companies - show as dropdown
                    <div className="select-wrapper border-blue">
                      <select
                        id="company"
                        value={selectedCompany}
                        onChange={(e) => {
                          setSelectedCompany(e.target.value);
                          setCompanyName(e.target.value);
                        }}
                        className="form-select"
                        style={inputStyle}
                      >
                        <option value="">Select Company</option>
                        {companies.map((company, index) => (
                          <option key={index} value={company.companyName}>
                            {company.companyName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="industry"> Industry <span style={{ color: "red" }}>*</span> </label>{" "}
                <div className="select-wrapper border-blue">
                  <select
                    id="industry"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="form-select"
                    style={inputStyle}
                  >
                    <option value="" disabled>
                      {" "}
                      Choose industry{" "}
                    </option>{" "}
                    <option value="manufacturing"> Manufacturing </option>{" "}
                    <option value="retail"> Retail & Wholesale Trade </option>{" "}
                    <option value="services"> Services </option>{" "}
                    <option value="food"> Food & Beverage </option>{" "}
                    <option value="construction">
                      {" "}
                      Construction & Real Estate{" "}
                    </option>{" "}
                    <option value="healthcare"> Healthcare </option>{" "}
                    <option value="agriculture">
                      {" "}
                      Agriculture & Allied Activities{" "}
                    </option>{" "}
                    <option value="education">
                      {" "}
                      Education & Training{" "}
                    </option>{" "}
                    <option value="transport">
                      {" "}
                      Transport & Logistics{" "}
                    </option>{" "}
                    <option value="technology">
                      {" "}
                      IT & Tech Services{" "}
                    </option>{" "}
                    <option value="tourism">
                      {" "}
                      Tourism & Hospitality{" "}
                    </option>{" "}
                    <option value="fashion"> Fashion & Apparel </option>{" "}
                    <option value="events"> Event Management </option>{" "}
                    <option value="ecommerce"> E - commerce Sellers </option>{" "}
                    <option value="printing"> Printing & Packaging </option>{" "}
                    <option value="beauty"> Beauty & Wellness </option>{" "}
                    <option value="automotive"> Automotive Services </option>{" "}
                    <option value="media">
                      {" "}
                      Media & Advertising Agencies{" "}
                    </option>{" "}
                    <option value="cleaning">
                      {" "}
                      Cleaning & Sanitation Services{" "}
                    </option>{" "}
                    <option value="handicrafts">
                      {" "}
                      Handicrafts & Artisan Units{" "}
                    </option>{" "}
                    <option value="other"> Others </option>{" "}
                  </select>{" "}
                </div>{" "}
              </div>{" "}
            </div>
            {/* Keywords in one row */}{" "}
            <div className="form-row">
              <div className="form-group" style={{ width: "100%", marginBottom: isMobile ? "10px" : "0px" }}>
                <label htmlFor="keywords"> Select your keywords to generate your tagline <span style={{ color: "red" }}>*</span> </label>{" "}
                <div className="keywords-input-container border-blue" style={{
                  minHeight: isMobile ? "auto" : "60px", /* Increased min-height for multiple keyword rows */
                  height: "auto",
                  maxHeight: isMobile ? "auto" : "80px", /* Constrain maximum height */
                  overflow: "auto"  /* Allow scrolling if too many keywords */
                }}>
                  <div className="keywords-tags">
                    {" "}
                    {keywords.map((keyword, index) => (
                      <div key={index} className="keyword-tag">
                        {" "}
                        {keyword}{" "}
                        <button
                          type="button"
                          className="remove-tag"
                          onClick={() => handleRemoveKeyword(keyword)}
                        >
                          ×{" "}
                        </button>{" "}
                      </div>
                    ))}{" "}
                  </div>{" "}
                </div>{" "}
                {/* Fixed height container that will always take up the same vertical space */}
                <div style={{
                  position: "relative",
                  width: "100%",
                  height: isMobile ? "auto" : "70px", /* Increased fixed height on desktop */
                  marginBottom: "0px",
                  maxHeight: isMobile ? "none" : "70px", /* Constrain maximum height */
                  overflow: "visible"
                }}>
                  <div className="suggested-keywords" style={{ 
                    position: "absolute",
                    top: "0",
                    left: "0",
                    width: "100%",
                    marginBottom: "0px", 
                    paddingBottom: "0px"
                  }}>
                    {/* Always render the container even if empty to maintain layout */}
                    {industryKeywords.length > 0 ? (
                      industryKeywords.slice(0, 10).map((keyword, index) => (
                        <button
                          key={index}
                          type="button"
                          className="suggested-keyword"
                          onClick={() => handleAddKeyword(keyword)}
                          disabled={keywords.includes(keyword)}
                          style={
                            keywords.includes(keyword)
                              ? { opacity: 0.5, cursor: "not-allowed" }
                              : {}
                          }
                        >
                          {keyword}{" "}
                        </button>
                      ))
                    ) : (
                      <div style={{ visibility: "hidden" }}>
                        {/* Hidden placeholder buttons to maintain space */}
                        {[1,2,3,4,5].map(i => (
                          <button key={i} className="suggested-keyword" style={{ visibility: "hidden" }}>
                            placeholder
                          </button>
                        ))}
                      </div>
                    )}{" "}
                  </div>
                </div>{" "}
              </div>{" "}
            </div>
            {/* Tagline in one row - pushed up with negative margin */}{" "}
            <div className="form-row" style={{ marginTop: isMobile ? "20px" : "30px" }}>
              <div
                className="form-group"
                style={{ width: "100%", marginBottom: "5px", marginTop: isMobile ? "0px" : "-3px" }}
              >
                <label
                  htmlFor="tagline"
                  style={{
                    fontSize: isMobile ? "12px" : "16px", // 12px for mobile, 16px for desktop
                    fontWeight: "500",
                    color: "#333",
                    display: "block",
                    marginBottom: isMobile ? "5px" : "2px", // Reduced bottom margin on desktop
                    marginTop: isMobile ? "0px" : "-5px", // Negative top margin on desktop
                    fontFamily: "Poppins, sans-serif",
                    maxHeight: isMobile ? "16px" : "18px", // Slightly reduced max-height
                    lineHeight: "1.2",
                    textTransform: "none"
                  }}
                >
                  Generate Tagline <span style={{ color: "red" }}>*</span>
                </label>{" "}
                <div
                  className="tagline-row"
                  style={{ flexDirection: "column", width: "100%" }}
                >
                  <div className="tagline-field" style={{ width: "100%" }}>
                    {" "}
                    {tagline ? (
                      <textarea
                        ref={taglineRef}
                        className="tagline-display border-blue"
                        // Display tagline without asterisks for cleaner UI
                        value={tagline.replace(/\*(.*?)\*/g, "$1")}
                        readOnly={true}
                        style={{
                          ...inputStyle,
                          height: "60px", // Fixed height, not auto
                          minHeight: "60px",
                          maxHeight: "60px", // Fixed max height equal to height
                          padding: "10px 12px",
                          overflow: "auto", /* Show scrollbar if text exceeds max height */
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          wordWrap: "break-word",
                          cursor: "text",
                          backgroundColor: "#f8f8f8",
                          lineHeight: "1.4",
                          fontSize: "14px",
                          resize: "none",
                          width: "100%",
                          border: "1px solid #ddd",
                        }}
                      />
                    ) : (
                      <input
                        type="text"
                        id="tagline"
                        placeholder="Your company tagline will appear here..."
                        value={tagline}
                        readOnly={true}
                        className="form-input border-blue"
                        style={{
                          ...inputStyle,
                          height: "38px",
                          lineHeight: "38px",
                        }}
                      />
                    )}{" "}
                  </div>{" "}
                  <div
                    className="tagline-actions"
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      justifyContent: "flex-start",
                      width: "100%",
                    }}
                  >
                    <button
                      type="button"
                      className="generate-btn"
                      onClick={handleGenerateTagline}
                      disabled={isGeneratingTagline || keywords.length === 0}
                      style={{
                        backgroundColor: "#0083B5",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "5px 15px",
                        height: "32px",
                        cursor:
                          keywords.length === 0 ? "not-allowed" : "pointer",
                        opacity: keywords.length === 0 ? 0.7 : 1,
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        marginTop: "5px",
                        width: window.innerWidth > 768 ? "25%" : "100%", // 25% width on desktop, 100% on mobile
                        maxWidth: window.innerWidth > 768 ? "25%" : "100%",
                      }}
                    >
                      {isGeneratingTagline ? (
                        <>
                          <i className="fas fa-spinner fa-spin"> </i>
                          Generating...{" "}
                        </>
                      ) : isTaglineGenerated ? (
                        <>
                          <i className="fas fa-redo-alt"> </i>
                          Regenerate{" "}
                        </>
                      ) : (
                        <>
                          <i className="fas fa-magic sparkle-icon"> </i>
                          Generate{" "}
                        </>
                      )}{" "}
                    </button>{" "}
                  </div>{" "}
                </div>{" "}
              </div>{" "}
            </div>
            <button
              className="next-btn"
              onClick={handleNext}
              style={{
                ...yellowButtonStyle,
                marginTop: "5px",
                marginBottom: "-20px",
              }}
            >
              Next{" "}
            </button>{" "}
          </div>{" "}
        </div>{" "}
      </div>{" "}
    </div>
  );
};

export default AddDetails;
